import React, { useRef, useEffect } from 'react';
import './RichTextEditor.css';

export default function RichTextEditor({ value, onChange, placeholder, rows = 10 }) {
  const editorRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current && value !== textareaRef.current.value) {
      textareaRef.current.value = value || '';
    }
  }, [value]);

  const execCommand = (command, value = null) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    let newText = '';
    
    // Handle font size dropdown
    if (command === 'fontSize' && value) {
      const sizeMap = { small: '14px', medium: '16px', large: '18px' };
      const fontSize = sizeMap[value] || '16px';
      newText = `<span style="font-size: ${fontSize}">${selectedText || 'Text'}</span>`;
      const beforeText = textarea.value.substring(0, start);
      const afterText = textarea.value.substring(end);
      const updatedValue = beforeText + newText + afterText;
      onChange(updatedValue);
      setTimeout(() => {
        const newCursorPos = start + newText.length;
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
      return;
    }

    switch (command) {
      case 'bold':
        newText = `<strong>${selectedText || 'Bold Text'}</strong>`;
        break;
      case 'italic':
        newText = `<em>${selectedText || 'Italic Text'}</em>`;
        break;
      case 'underline':
        newText = `<u>${selectedText || 'Underlined Text'}</u>`;
        break;
      case 'h3':
        newText = `<h3>${selectedText || 'Heading'}</h3>`;
        break;
      case 'h4':
        newText = `<h4>${selectedText || 'Subheading'}</h4>`;
        break;
      case 'ul':
        newText = `<ul>\n<li>${selectedText || 'List item'}</li>\n</ul>`;
        break;
      case 'ol':
        newText = `<ol>\n<li>${selectedText || 'List item'}</li>\n</ol>`;
        break;
      case 'li':
        newText = `<li>${selectedText || 'List item'}</li>`;
        break;
      case 'p':
        newText = `<p>${selectedText || 'Paragraph'}</p>`;
        break;
      case 'br':
        newText = '<br />';
        break;
      default:
        return;
    }

    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);
    const updatedValue = beforeText + newText + afterText;
    
    onChange(updatedValue);
    
    // Restore cursor position
    setTimeout(() => {
      const newCursorPos = start + newText.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <div className="rich-text-editor">
      <div className="rich-text-toolbar">
        <button
          type="button"
          className="rich-text-btn"
          onClick={() => execCommand('bold')}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className="rich-text-btn"
          onClick={() => execCommand('italic')}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className="rich-text-btn"
          onClick={() => execCommand('underline')}
          title="Underline"
        >
          <u>U</u>
        </button>
        <div className="rich-text-separator" />
        <button
          type="button"
          className="rich-text-btn"
          onClick={() => execCommand('h3')}
          title="Heading"
        >
          H3
        </button>
        <button
          type="button"
          className="rich-text-btn"
          onClick={() => execCommand('h4')}
          title="Subheading"
        >
          H4
        </button>
        <div className="rich-text-separator" />
        <button
          type="button"
          className="rich-text-btn"
          onClick={() => execCommand('ul')}
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          className="rich-text-btn"
          onClick={() => execCommand('ol')}
          title="Numbered List"
        >
          1. List
        </button>
        <button
          type="button"
          className="rich-text-btn"
          onClick={() => execCommand('li')}
          title="List Item"
        >
          • Item
        </button>
        <div className="rich-text-separator" />
        <button
          type="button"
          className="rich-text-btn"
          onClick={() => execCommand('p')}
          title="Paragraph"
        >
          ¶
        </button>
        <button
          type="button"
          className="rich-text-btn"
          onClick={() => execCommand('br')}
          title="Line Break"
        >
          ↵
        </button>
        <div className="rich-text-separator" />
        <select
          className="rich-text-select"
          onChange={(e) => execCommand('fontSize', e.target.value)}
          title="Font Size"
          defaultValue=""
        >
          <option value="" disabled>Size</option>
          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
        </select>
      </div>
      <textarea
        ref={textareaRef}
        className="rich-text-textarea"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
      />
      <small className="rich-text-hint">
        Use toolbar buttons to format text. HTML tags will be inserted automatically.
      </small>
    </div>
  );
}

